import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box, ModalHeader, Modal, ModalFooter, RadioGroup, FileSelector, FileSelectorFileType } from '@parspec/pixel';

import { saveDataToSessionStorage } from 'src/features/shared/utils/utils';
import { useAddSuggestionMutation } from '../../shared/DocumentSuggestions/queries';
import { useUploadDifferentFile } from '../ImportMoreProducts/queries';
import { ReuseDialogType, ReuseDialogTypeLabel } from './utils';
import { environment } from 'src/environments';
import { ADD_REUSE_ACCEPTED_FORMATS } from './constants';

const typeOptions = [
    { value: ReuseDialogType.DATASHEET, label: ReuseDialogTypeLabel.datasheet },
    { value: ReuseDialogType.WARRANTY, label: ReuseDialogTypeLabel.warranty },
    { value: ReuseDialogType.GUIDE, label: ReuseDialogTypeLabel.installation_guide },
    { value: ReuseDialogType.DRAWING, label: ReuseDialogTypeLabel.drawing },
    { value: ReuseDialogType.OTHER, label: ReuseDialogTypeLabel.other_document }
];

const howOptions = [
    { value: 'reuse', label: 'Reuse from another product in this BoM' },
    { value: 'upload', label: 'Upload a new document' }
];
const UPLOAD_EXISTING_VALUE = 'uploadExisting';

interface AddReuseDocumentProps {
    open: boolean;
    onClose: () => void;
    onReuseConfirm: (value: { reuse: boolean; document?: ReuseDialogType }) => void;
    selectedRowIds: Array<number>;
};

interface IFileType {
    file: File | FileSelectorFileType;
    s3_file_path?: string;
}

const subTitle = <Box>Select the document type to add, and how you’d like to add it.</Box>;

const AddReuseDocument = (props: AddReuseDocumentProps) => {
    const navigate = useNavigate();
    const { open, onClose, onReuseConfirm: confirmAction, selectedRowIds } = props;
    const [typeselectedValue, setTypeSelectedValue] = useState(ReuseDialogType.DATASHEET);
    const { bomId, projectId } = useParams();
    const [uploadedFile, setUploadedFile] = useState<File[] | IFileType[]>([]);
    const [fileRequiredError, setFileRequiredError] = useState<string | null>(null);
    const [modeselectedValue, setModeSelectedValue] = useState('reuse');
    const [uploadedFilePath, setUploadedFilePath] = useState<string>();

    const { isLoading, mutateAsync: addDocument } = useAddSuggestionMutation();

    const { isLoading: submittalUploadLoading, mutateAsync: uploadExistingSubmittal } = useUploadDifferentFile();

    const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTypeSelectedValue(() => event.target.value as ReuseDialogType);
        setFileRequiredError('');
        setUploadedFile([]);
        setUploadedFilePath('');
        setModeSelectedValue('reuse');
    };

    const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setModeSelectedValue(event.target.value);
        setUploadedFile([]);
        setFileRequiredError('');
        setUploadedFilePath('');
    };

    const handleFileUploadedToS3 = (files: IFileType[]) => {
        if (files.length) {
            setUploadedFile(files);
            setUploadedFilePath(files[0].s3_file_path);
            setFileRequiredError('');
            return;
        }
        setFileRequiredError('');
        setUploadedFilePath('');
    };

    const handleUploadExistingSubmittal = async () => {
        const formData = new FormData();
        if (!uploadedFile?.length) {
            setFileRequiredError('A file is required for the option selected.');
            return;
        }
        formData.append('file_info', 'media/temp_files/' + uploadedFilePath!);
        try {
            const res = await uploadExistingSubmittal({
                packageType: 'submittal_package',
                bom_id: Number(bomId),
                data: formData
            });
            saveDataToSessionStorage('selectedBodIds', JSON.stringify(selectedRowIds));
            navigate(`/v2/project/${projectId}/bom/${bomId}/adjustPages?showPopup=${true}&submittalPackageId=${res?.data?.id}&from=bom-detail`);
        } catch (error: any) {
            setFileRequiredError(error?.response?.data?.message || 'Something went wrong!');
        }
    };

    const onConfirm = async () => {
        if (modeselectedValue === 'reuse') {
            return confirmAction({ reuse: true, document: typeselectedValue });
        }
        if (modeselectedValue === UPLOAD_EXISTING_VALUE) {
            return handleUploadExistingSubmittal();
        }
        if (!uploadedFile?.length) {
            setFileRequiredError('File is required for above option');
            return;
        }
        const formData: FormData = new FormData();
        formData.append('input', uploadedFile[0] as File);
        formData.append('bod_id', `[${selectedRowIds}]`);
        try {
            await addDocument({
                input: formData,
                bomId: bomId || '',
                documentType: typeselectedValue === ReuseDialogType.DATASHEET ? 'submittal' : 'o_m',
                subDocumentType: typeselectedValue,
                actionType: 'file_upload',
                productId: selectedRowIds
            });
            confirmAction({ reuse: false });
        } catch (error: any) {
            setFileRequiredError(error?.response?.data?.message || 'Something went wrong!');
        }
    };

    const howToAddOptions = useMemo(() => {
        const addOptions = [...howOptions];
        if (typeselectedValue === ReuseDialogType.DATASHEET) {
            addOptions.splice(1, 0, { label: 'From an existing submittal', value: UPLOAD_EXISTING_VALUE });
        }
        return addOptions;
    }, [typeselectedValue]);

    return (
        <Modal
            open={open}
            header={<ModalHeader title="Add or Reuse Document" onClose={onClose} children={subTitle} />}
            footer={<ModalFooter continueButtonLabel="Confirm" onAccept={onConfirm} onReject={onClose} isLoading={isLoading || submittalUploadLoading} />}
        >
            <Box width={'467px'}>
                <RadioGroup row={false} label={'Which document type are you adding to these products?'} name={'type'} options={typeOptions} onChange={handleTypeChange} value={typeselectedValue} />
                <RadioGroup row={false} label={'How are you adding this document?'} name={'mode'} options={howToAddOptions} onChange={handleModeChange} value={modeselectedValue} />
                {modeselectedValue === 'upload' && (
                    <FileSelector
                        placeholder="Drag and drop a file here, or:"
                        onSelect={(files: FileSelectorFileType[] | File[]) => {
                            setFileRequiredError('');
                            setUploadedFile(files as File[]);
                        }}
                        acceptedFormats={ADD_REUSE_ACCEPTED_FORMATS}
                        helperText={'Supported file types: PDF'}
                        error={fileRequiredError || ''}
                    ></FileSelector>
                )}
                {modeselectedValue === UPLOAD_EXISTING_VALUE && (
                    <FileSelector
                        placeholder="Drag and drop a file here, or:"
                        url={`${environment.b}/api/generate_signed_url/`}
                        onUpload={handleFileUploadedToS3}
                        onSelect={() => setFileRequiredError('')}
                        acceptedFormats={ADD_REUSE_ACCEPTED_FORMATS}
                        helperText={'Supported file types: PDF'}
                        error={fileRequiredError || ''}
                    />
                )}
            </Box>
        </Modal>
    );
};

export default AddReuseDocument;
